import React from 'react';
import { ArrowRight } from 'lucide-react';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  withArrow?: boolean;
}

const Button: React.FC<ButtonProps> = React.memo(({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  fullWidth = false,
  withArrow = false,
  className = '',
  disabled = false,
  ...props
}) => {
  const baseStyles = 'inline-flex items-center justify-center font-medium transition-colors rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary';
  
  const variantStyles = {
    primary: disabled 
      ? 'bg-gray-50 text-gray-400 cursor-not-allowed' 
      : 'bg-primary text-white hover:bg-blue-600 shadow-sm',
    secondary: disabled
      ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
      : 'bg-blue-100 text-primary hover:bg-blue-200',
    outline: disabled
      ? 'border border-gray-300 text-gray-400 cursor-not-allowed bg-gray-50'
      : 'border border-gray-300 text-gray-700 hover:bg-gray-50',
    ghost: disabled
      ? 'text-gray-400 cursor-not-allowed'
      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
  };
  
  const sizeStyles = {
    sm: 'text-sm px-3 py-1.5',
    md: 'text-base px-4 py-2',
    lg: 'text-lg px-6 py-3',
  };
  
  const widthStyles = fullWidth ? 'w-full' : '';
  
  const styles = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthStyles} ${className}`;
  
  return (
    <button className={styles} disabled={disabled} {...props}>
      {icon && iconPosition === 'left' && <span className="mr-2">{icon}</span>}
      <span>{children}</span>
      {withArrow && <ArrowRight className="ml-2 h-4 w-4" />}
      {icon && iconPosition === 'right' && <span className="ml-2">{icon}</span>}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;